import throttle from 'lodash.throttle';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IncPageContext } from '../inc-page.context';
import {
  IncPageTabsContext,
  ScrollContainerContext,
} from './inc-page-tabs.context';
import useResizeObserver from 'use-resize-observer';
import { sectionOffset } from './inc-page-section/inc-page-section.component';

export const useIncPageTabs = defaultTab => {
  const headerRef = useRef();
  const sectionRefs = useRef([]);

  const [lastRef, setLastRef] = useState();

  const registerSection = useCallback((ref, last) => {
    if (!ref || sectionRefs.current.find(sectionRef => sectionRef === ref))
      return;

    if (last) {
      setLastRef(ref);
    }

    sectionRefs.current = [...sectionRefs.current, ref];
  }, []);

  return {
    registerSection,
    headerRef,
    sectionRefs,
    defaultTab,
    lastRef,
  };
};

export const useIncPageTabsContext = () => {
  return useContext(IncPageTabsContext);
};

export const useIncPageTabsComponent = () => {
  const { headerRef, sectionRefs, defaultTab, lastRef } =
    useIncPageTabsContext();
  const [currentTab, setCurrentTab] = useState(defaultTab);
  const scrollContainerRef = useScrollContainerRef();
  const { hasFooter } = useContext(IncPageContext);

  const { height: headerHeight } = useResizeObserver({
    ref: headerRef,
    box: 'border-box',
  });
  const { height: lastSectionHeight } = useResizeObserver({
    ref: lastRef,
    box: 'border-box',
  });
  const { height: scrollContainerHeight } = useResizeObserver({
    ref: scrollContainerRef,
    box: 'border-box',
  });

  const registerTarget = useCallback(
    ref => {
      if (!ref || ref === headerRef.current) return;

      headerRef.current = ref;
    },
    [headerRef],
  );

  const callback = useCallback(() => {
    const targetOffset =
      headerRef.current.getBoundingClientRect().top +
      headerRef.current.offsetHeight;
    let current = sectionRefs.current[0];
    sectionRefs.current?.find(sectionRef => {
      if (sectionRef.getBoundingClientRect().top <= targetOffset) {
        current = sectionRef;
        return false;
      } else return true;
    });

    setCurrentTab(current?.id);
  }, [sectionRefs, headerRef]);

  useLayoutEffect(() => {
    if (
      scrollContainerHeight != null &&
      headerHeight != null &&
      lastSectionHeight != null &&
      lastRef
    ) {
      lastRef.style.marginBottom = `${Math.max(
        scrollContainerHeight -
          headerHeight -
          lastSectionHeight -
          (hasFooter ? 94 : 0),
        0,
      )}px`;
    }
  }, [
    hasFooter,
    headerHeight,
    lastRef,
    lastSectionHeight,
    scrollContainerHeight,
  ]);

  useLayoutEffect(() => {
    if (headerHeight != null && scrollContainerRef.current) {
      scrollContainerRef.current.style.scrollPaddingTop = `${
        headerHeight - sectionOffset
      }px`;
    }

    return () => {
      if (scrollContainerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        scrollContainerRef.current.style.scrollPaddingTop = '0px';
      }
    };
  }, [headerHeight, scrollContainerRef]);

  useThrottledOnScroll(callback, scrollContainerRef, { delay: 166 });

  return { registerTarget, currentTab, setCurrentTab };
};

const useThrottledOnScroll = (callback, scrollTarget, { delay }) => {
  const throttledCallback = useMemo(
    () => throttle(callback, delay),
    [callback, delay],
  );

  useEffect(() => {
    const st = scrollTarget.current;

    st.addEventListener('scroll', throttledCallback);
    return () => {
      st.removeEventListener('scroll', throttledCallback);
      throttledCallback.cancel();
    };
  }, [scrollTarget, throttledCallback]);
};

export default useThrottledOnScroll;

export const useScrollContainer = () => useRef();

export const useScrollContainerRef = () => useContext(ScrollContainerContext);
